/** @module ChangeTracking/ValueRenderer */
define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/dom-class",
    "csi",
    "csi/_base/underscore",
    "csi/def",
    "csi/heading/_base",
    "csi/heading/Attribute",
    "csi/heading/Ref",
    "csi/heading/RefContainer",
    "csi/html",
    "csi/locale",
    "csi/xml"
], function(declare,lang,domClass,csi,_,def,csiHeading,Attribute,Ref,RefContainer,html,locale,xml) {

    var PROMPTS = {
        NewValue: "Added",
        OldValue: "Removed"
    };

    /**
    * Return the NameContext node corresponding to 'refUrl'
     * @param {Context} ctx
     * @param {CNL} refUrl - a reference to NameContext node
     * @return {Node}
    */
    function getNameContextNode(ctx, refUrl) {
        return refUrl ? ctx.cache.getNode(refUrl) : null;
    }

    /**
     * Return the fake client node to navigate to, create an empty centric: ref
     * if nameContext isn't set
     * @param {Node} nameContext
     * @return {Node}
     */
    function createClientNodeFromNameContext(nameContext) {
        return nameContext ? xml.nodeCreateClientNode(nameContext.ActualNode, nameContext.ActualType, nameContext.$Name) : null;
    }

    /**
    * If ChangeLogAttributeEntry is a reference,
    * convert the value to a reference to ChangeLogNameContext
    * and then use 'ActualNode' of ChangeLogNameContext to create a link to a real node
    */
    var _RefRenderer = declare(Ref, {

        _getReferencedNode: function(/*Context*/ctx, /*Heading*/h) {
            var attrEntry = ctx.getNode();
            var value = attrEntry[h.m_id];
            return getNameContextNode(ctx, value);
        },

        _getLinkNode: function(/*Context*/ctx, /*Heading*/h, /*Node*/source) {
            return !source.Deleted && def.canNavigateToType(source.ActualType) ? createClientNodeFromNameContext(source) : null;
        },

        /**
         * @override
         * Add code to the display name
         */
        getNodeDisplayName: function(ctx, node) {
            var displayName = this.inherited(arguments);
            if (!ctx.view.get("showDetails") || !node || !node.Code) {
                return displayName;
            }

            return displayName + "(" + node.Code + ")";
        }
    });

    var _refRen = new _RefRenderer();

    /**
     * If ChangeLogAttributeEntry is a reference container,
     * convert the value to a reference container to ChangeLogNameContext-s
     * and then use 'ActualNode' of ChangeLogNameContext to create links to real nodes
     */
    var _RefContainerRenderer = declare(RefContainer, {

        getReferencedNodes: function(/*Context*/ctx, /*Heading*/h) {
            var attrEntry = ctx.getNode();
            var value = attrEntry[h.m_id];
            return _.map(value, _.partial(getNameContextNode, ctx));
        },

        _getLinkNode: function(/*Context*/ctx, /*Heading*/h, /*Node*/source) {
            return !source.Deleted && def.canNavigateToType(source.ActualType) ? createClientNodeFromNameContext(source) : null;
        },

        /**
         * @override
         * Add code to the display name
         */
        getNodeDisplayName: function(ctx, node) {
            var displayName = this.inherited(arguments);
            if (!ctx.view.get("showDetails") || !node || !node.Code) {
                return displayName;
            }

            return displayName + "(" + node.Code + ")";
        }
    });

    var _refContainerRen = new _RefContainerRenderer();

    return declare(Attribute, {
        /**
        * Disable value filtering for now
        * @param {Context} ctx
        * @param {Heading} h
        * @param {Spec} spec
        * @return {Object}
        */
        getQuerySpec: function() {
            return null;
        },

        /**
        * Render heading in view using typed attribute value rather than string value referenced by given heading
        * @param {Context} ctx - view context
        * @param {Heading} h - heading to render
        * @param {DOMNode} refNode - DOMNode where to render heading
        * @return {boolean} true if value was rendered
        */
        headingToHtml: function(ctx, h, refNode) {
            var logEntry = ctx.getNodeContext();
            var attrEntry = ctx.getNode();
            var format = attrEntry.AttributeFormat;
            var type = attrEntry.AttributeType;
            var ren = csiHeading.getRenderer(type, format);
            var value = attrEntry[h.m_id];

            // Case when value is not available (vs blank). Usually means first change after tracking enabled on this attribute, or op=created
            if (value==null){
            	domClass.add(refNode, html.CSS_NOT_APPLICABLE);
            	return;
            }

            // replace 'Ref' renderers with our versions
            if (ren instanceof Ref) {
                ren = _refRen;
            } else if(ren instanceof RefContainer) {
                ren = _refContainerRen;
            }

            // update heading with format for attribute entry and reset later
            h.m_format = format;

            if (type == xml.ATTR_BOOLEAN) {
                // render true/false rather than checkbox
                domClass.add(refNode, h.m_class);
                html.text(refNode, ren.headingToText(ctx, h));
            } else if (!xml.attributeTypeIsContainer(type)) {
                // render heading as normal
                ren.headingToHtml(ctx, h, refNode);
            } else if (!_.isEmpty(value)) {
                if (_.isArray(value)) {
                    // render Added/Removed prompt for container values if not a Created operation
                    if (logEntry.Operation == "ChangeTrackingType:Updated") {
                        html.create("span", {
                            "class": "bold",
                            innerText: locale.resource(PROMPTS[h.m_id]) + ": "
                        }, refNode);
                    }

                    ren.headingToHtml(ctx, h, refNode);
                } else {
                    // render table of key/value pairs
                    var table = html.create("table", {"class": "csi-heading-table"});
                    var tr = html.create("tr", null, table);
                    var renBase;
                    var hBase;

                    html.create("th", {innerText: locale.resource("Key")}, tr);
                    html.create("th", {innerText: locale.resource("Value")}, tr);

                    _.each(value, function(x, key) {
                        ctx.keyValue = key;

                        renBase = renBase || ren.getBaseRenderer(ctx, h);
                        hBase = hBase || ren.getBaseHeading(ctx, h);

                        tr = html.create("tr", null, table);
                        html.create("td", {innerText: String(key)}, tr);

                        renBase.headingToHtml(ctx, hBase, html.create("td", null, tr));
                    });

                    ctx.keyValue = null;
                    refNode.appendChild(table);
                }
            }

            h.m_format = null;
        }
    });
});
